// The tutorial on screen: Oga Danfo, the park's old plate-01 keke, talks you through it in a speech
// bubble. The tip for the step in hand sits in the same flow as every other prompt (the HUD's prompt
// column, the lobby, the meeting board), so it never covers one. It says what to do, points the way
// to where it happens, and makes the button it is about glow. The welcome and the last card are
// boards of their own, shown alone.
import { TILE } from '@wahala/maps';
import { type ReactNode, useEffect, useMemo, useReducer, useState } from 'react';
import type { IconName } from '../ds/wahala';
import { useUi } from '../store/ui';
import type { World } from '../store/world';
import { S } from '../strings';
import { Html, W } from '../ui/wahala';
import { endPractice } from '.';

/** Draw again whenever the world changes (the tip, and the arrow as you drive). */
function useWorldTicks(world: World | undefined): void {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => world?.subscribe(bump), [world]);
}

const still = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** The line types out the way a game tutor talks (about 60 letters a second); a tap shows it all.
 *  Reduced motion shows it at once. */
export function useTyped(text: string): [string, boolean, () => void] {
  const [n, setN] = useState(() => (still() ? text.length : 0));
  // a new line starts from the top
  useEffect(() => {
    if (still()) {
      setN(text.length);
      return;
    }
    setN(0);
    const t = setInterval(() => {
      setN((v) => {
        if (v >= text.length) clearInterval(t);
        return Math.min(text.length, v + 2);
      });
    }, 33);
    return () => clearInterval(t);
  }, [text]);
  return [text.slice(0, n), n >= text.length, () => setN(text.length)];
}

/** Oga Danfo's bubble, compact for play: his keke, the title and Skip on one line, the line he says
 *  under it, and a last row only when there is a way to point or a step to tap. The keke drives
 *  (state go) while he talks and rests when done. */
function Tutor({
  title,
  text,
  extra,
  actions,
}: {
  title: string;
  text: string;
  extra?: ReactNode;
  actions?: ReactNode;
}) {
  const [shown, done, finish] = useTyped(text);
  const keke = useMemo(() => W().keke({ player: 'danfo', state: done ? 'idle' : 'go', size: 44 }), [done]);
  return (
    <section className="tutor coach" aria-live="polite" aria-label={`${S.practice.tutor}: ${title}`}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: tapping the bubble only hurries the typing; the full line is already in the page for readers */}
      <div className="tutor__bubble" onClick={finish}>
        {/* his portrait: the keke in a danfo badge at the bubble's corner */}
        <Html className="tutor__who" html={keke} />
        <div className="tutor__head">
          <h2 className="tutor__title">{title}</h2>
          <Skip />
        </div>
        {/* the whole line holds the bubble's size from the first letter, so it never grows as it types */}
        <p className="tutor__text">
          <span className="tutor__ghost" aria-hidden="true">
            {text}
          </span>
          <span aria-hidden="true">
            {shown}
            {!done && <span className="tutor__caret" />}
          </span>
          <span className="sr-only">{text}</span>
        </p>
        {(extra || actions) && (
          <div className="tutor__row">
            {extra}
            {actions}
          </div>
        )}
      </div>
    </section>
  );
}

const Skip = () => (
  <button type="button" className="coach__skip" aria-label={S.practice.skip} onClick={() => endPractice(true)}>
    {S.practice.skipShort}
  </button>
);

export function CoachTip({ world }: { world: World }) {
  const director = useUi((s) => s.practice);
  useWorldTicks(world);
  const c = world.coach;
  const focus = director?.focus() ?? null;
  // the button the tip is about glows (a style rule keyed on its label)
  useEffect(() => {
    if (!focus) return;
    const el = document.createElement('style');
    el.textContent = `.hud__actions .wh-hud[aria-label="${CSS.escape(focus)}"] .wh-hud__disc{animation:coach-glow 1.1s ease-in-out infinite;box-shadow:0 0 0 4px var(--danfo,#f7c600)}`;
    document.head.append(el);
    return () => el.remove();
  }, [focus]);
  if (!director || !c) return null;
  const target = director.target();
  const me = world.view?.you.pos;
  let way: { angle: number; text: string } | null = null;
  if (target && me) {
    const dx = target.pos.x - me.x;
    const dy = target.pos.y - me.y;
    const tiles = Math.round(Math.hypot(dx, dy) / TILE);
    way = {
      angle: (Math.atan2(dy, dx) * 180) / Math.PI,
      text: tiles <= 1 ? S.practice.here(target.label) : S.practice.far(target.label, tiles),
    };
  }
  return (
    <Tutor
      title={c.title}
      text={c.text}
      extra={
        way && (
          <p className="coach__way">
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              aria-hidden="true"
              style={{ transform: `rotate(${way.angle}deg)` }}
            >
              <path d="M3 12h14M12 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <span>{way.text}</span>
          </p>
        )
      }
      actions={
        c.next && (
          <button type="button" className="wh-btn wh-btn--carry wh-btn--sm" onClick={() => director.next()}>
            {c.next}
          </button>
        )
      }
    />
  );
}

/** The welcome's convoy: four paints on the road, one of them a knocked engine. */
const CONVOY = [
  { player: 'zobo', load: 'plantain', state: 'go' },
  { player: 'palm', state: 'knocked' },
  { player: 'danfo', load: 'speaker', state: 'go' },
] as const;

/** The end card's road: Oga Danfo leads the two kekes you drove (your driver, then your agbero)
 *  over the finish line. */
const ARRIVAL = [
  { player: 'jollof', state: 'agbero', size: 74 },
  { player: 'lagoon', load: 'luggage', state: 'go', size: 80 },
  { player: 'danfo', state: 'go', size: 86 },
] as const;

/** One side's lesson: its keke, its band, and three lines each with the button's icon. */
function Recap({
  label,
  keke,
  lines,
  icons,
  agbero = false,
}: {
  label: string;
  keke: string;
  lines: readonly string[];
  icons: readonly IconName[];
  agbero?: boolean;
}) {
  return (
    <div className={agbero ? 'welcome__role welcome__role--agbero done__role' : 'welcome__role done__role'}>
      <p className="welcome__band">{label}</p>
      <Html html={keke} />
      <ul className="done__list">
        {lines.map((line, i) => (
          <li key={line}>
            <Html className="done__icon" html={W().icon(icons[i] ?? 'check', { size: 14 })} />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The last board: the convoy crosses the finish, Oga Danfo says his last line (typed, like his
 *  tips), the two sides' lessons, then play for real. */
function DoneCard() {
  const P = S.practice;
  const [shown, typed, finish] = useTyped(P.done.say);
  const badge = useMemo(() => W().keke({ player: 'danfo', state: typed ? 'idle' : 'go', size: 44 }), [typed]);
  return (
    <div
      className="practice-card practice-card--welcome"
      role="dialog"
      aria-modal="true"
      aria-labelledby="practice-done"
    >
      <section className="card stack welcome done">
        <header className="welcome__hero">
          <Html className="welcome__sticker" html={W().sticker({ id: 'shine-eye', size: 72 })} />
          <h2 id="practice-done" className="display welcome__title done__title">
            {P.done.title}
          </h2>
          <div className="welcome__road done__road" aria-hidden="true">
            <span className="done__finish" />
            {ARRIVAL.map((k) => (
              <Html key={k.player} className="welcome__keke done__keke" html={W().keke(k)} />
            ))}
          </div>
        </header>
        {/* Oga Danfo's last word, in his bubble */}
        <div className="tutor done__say">
          {/* biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: tapping only hurries the typing; the full line is in the page for readers */}
          <div className="tutor__bubble" onClick={finish}>
            <Html className="tutor__who" html={badge} />
            <p className="tutor__title">{P.tutor}</p>
            <p className="tutor__text">
              <span className="tutor__ghost" aria-hidden="true">
                {P.done.say}
              </span>
              <span aria-hidden="true">
                {shown}
                {!typed && <span className="tutor__caret" />}
              </span>
              <span className="sr-only">{P.done.say}</span>
            </p>
          </div>
        </div>
        <div className="welcome__roles">
          <Recap
            label={P.done.driverLabel}
            keke={W().keke({ player: 'lagoon', load: 'luggage', size: 80 })}
            lines={P.done.driver}
            icons={['stop', 'fix', 'shout']}
          />
          <Recap
            label={P.done.agberoLabel}
            keke={W().keke({ player: 'jollof', state: 'agbero', size: 80 })}
            lines={P.done.agbero}
            icons={['knock', 'wahala', 'shout']}
            agbero
          />
        </div>
        <div className="btn-row">
          <button
            type="button"
            className="wh-btn wh-btn--carry wh-btn--md"
            onClick={() => {
              endPractice(false);
              window.dispatchEvent(new Event('wahala:quickplay'));
            }}
          >
            {P.done.play}
          </button>
          <button type="button" className="wh-btn wh-btn--quiet wh-btn--md" onClick={() => endPractice(false)}>
            {P.done.menu}
          </button>
        </div>
      </section>
    </div>
  );
}

/** The boards before and after: the welcome (what the game is) and the end (go and play). */
export function PracticeCards() {
  const director = useUi((s) => s.practice);
  const park = useUi((s) => s.park);
  useWorldTicks(park?.world);
  if (!director) return null;
  const P = S.practice;
  if (director.step === 'welcome') {
    return (
      <div
        className="practice-card practice-card--welcome"
        role="dialog"
        aria-modal="true"
        aria-labelledby="practice-title"
      >
        <section className="card stack welcome">
          {/* the park at a glance: the title on danfo, a convoy on the road, the agbero hiding among them */}
          <header className="welcome__hero">
            <Html className="welcome__sticker" html={W().sticker({ id: 'oya', size: 72 })} />
            <h2 id="practice-title" className="display welcome__title">
              {P.welcome.title}
            </h2>
            <div className="welcome__road" aria-hidden="true">
              {CONVOY.map((k) => (
                <Html key={k.player} className="welcome__keke" html={W().keke({ size: 86, ...k })} />
              ))}
            </div>
          </header>
          <p>{P.welcome.body}</p>
          <div className="welcome__roles">
            <div className="welcome__role">
              <p className="welcome__band">{P.welcome.driverLabel}</p>
              <Html html={W().keke({ player: 'lagoon', load: 'luggage', size: 96 })} />
              <p>{P.welcome.driver}</p>
            </div>
            <div className="welcome__role welcome__role--agbero">
              <p className="welcome__band">{P.welcome.agberoLabel}</p>
              <Html html={W().keke({ player: 'jollof', state: 'agbero', size: 96 })} />
              <p>{P.welcome.agbero}</p>
            </div>
          </div>
          <p className="muted small">{P.welcome.note}</p>
          {/* start and skip side by side: start is the one danfo */}
          <div className="btn-row">
            <button type="button" className="wh-btn wh-btn--carry wh-btn--md" onClick={() => director.begin()}>
              {P.welcome.start}
            </button>
            <button type="button" className="wh-btn wh-btn--quiet wh-btn--md" onClick={() => endPractice(true)}>
              {P.skip}
            </button>
          </div>
        </section>
      </div>
    );
  }
  if (director.step === 'done') return <DoneCard />;
  return null;
}
